import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../@types/redux';
import { dataSetGrades } from '../../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../../services/appFunctionsService';
import { ICommodity } from '../../../../@types/model/masterData/commodity/commodity';
import GradeHttpService from '../../../../services/http/masterData/gradeHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../../store/general/Functions';
import GradeForm from './form/GradeForm';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../../@types/other';
import { setGradeMasterDataIndexedDB, syncMasterData } from '../../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import GradeModelHelper from '../../../../@types/model/masterData/grade/gradeModelHelper';
import { RightGrantsEnum } from '../../../../@types/enum/rightGrantsEnum';
import { IColour } from '../../../../@types/model/masterData/colour/colour';
import { IGrade } from '../../../../@types/model/masterData/grade/grade';
import { IGradeFormValues } from '../../../../@types/model/masterData/grade/gradeFormValues';
import UserHelper from '../../../../services/helper/userHelper';

interface IGradeScreenProps {
    selectedCommodity : ICommodity | null;
}

const GradeScreen = (props : IGradeScreenProps) : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const grades = useAppSelector(x => x.masterData.grades);
    const commodities = useAppSelector(x => x.masterData.commodities);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedGrade, setSelectedGrade] = useState<IGrade | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingGrade, setDeletingGrade] = useState<IGrade | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await GradeHttpService.getGradeData();
                dispatch(dataSetGrades(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading grades.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IGradeFormValues) => {
        setIsLoading(true);

        try {
            const upsert = GradeModelHelper.createUpsert(values);
            const res = await GradeHttpService.addOrUpdateGrade(upsert);
            const updatedList = upsertArrayElement(grades, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetGrades(updatedList));
            await setGradeMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedGrade ? 'Grade updated successfully.' : 'Grade added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating grade data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeGrade = async () => {
        if (!deletingGrade) return;
        const updatedGrade = { ...deletingGrade, isActive: false };
        setIsLoading(true);

        try {
            const res = await GradeHttpService.deleteGrade(updatedGrade.id);
            const updatedList = upsertArrayElement(grades, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetGrades(updatedList));
            await setGradeMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Grade deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting grade.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedGrade(null);
    };

    const edit = (selectedRow : IGrade) => {
        setSelectedGrade(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IGrade) : void => {
        setDeletingGrade(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingGrade(null);
    };

    const addButtonTooltip = () : string => {
        let message = 'Add';

        if (!userHasRights.addRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Grade', RightGrantsEnum.Add);
        } else if (!props.selectedCommodity) {
            message = 'Commodity Not Selected';
        }

        return message;
    };

    const deleteButtonTooltip = (row : IColour) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Colour', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const formatCommodities = useCallback((commodityIds : Array<number>) => {
        let string = commodities
            .filter(x => commodityIds.some(y => y === x.id))
            .map(x => x.code).toString().replace(/,/g, ', ');
        if (string.length > 85) {
            string = string.substring(0, 86) + '...';
        }
        return string;
    }, [commodities]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return GradeModelHelper.createFormValues(selectedGrade, commodities, props.selectedCommodity);
        }
    }, [selectedGrade, isDialogOpen, commodities, props.selectedCommodity]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const rows = useMemo(() => {
        return props.selectedCommodity
            ? grades.filter(x => x.commodityIds.some(y => y === props.selectedCommodity?.id))
            : grades;
    }, [props.selectedCommodity, grades]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <>
            <Card className={'fdc hfill'}>
                <CustomTable<IGrade>
                        
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || !props.selectedCommodity || isLoading}
                    addTooltip={addButtonTooltip()}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Grade', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : IGrade) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}
                
                    enableSorting
                    enableFiltering
                    enablePagination
                    fitWidthToPage
                    columns={[
                        { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Commodities', field: 'commodityIds', formatFunction: formatCommodities, width: 300, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={rows}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={340}
                    isActive={row => row.isActive}
                />
            </Card>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedGrade ? 'Edit Grade' : 'Add Grade'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <GradeForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Grade'}
                open={!!deletingGrade}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeGrade}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </>
    );
};

export default GradeScreen;
